import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Box,
  IconButton,
  LinearProgress,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import PasswordBus from "./PasswordBus";
import "./Login2.css";
import Avtar from "./img/avatar.svg";
import Bg from "./img/bg.svg";
import WaveImg from "./img/wave.png";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { MAIN_URL } from "../../data/const";

// Password strength calculation function
const calculatePasswordStrength = (password) => {
  let score = 0;
  const checks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    numbers: /\d/.test(password),
    symbols: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  // Add points for each criteria met
  Object.values(checks).forEach((check) => {
    if (check) score += 20;
  });

  return {
    score,
    checks,
    strength: score < 40 ? "weak" : score < 70 ? "medium" : "strong",
    color: score < 40 ? "#f44336" : score < 70 ? "#ff9800" : "#4caf50",
  };
};

export const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    checks: {},
    strength: "",
    color: "#f44336",
  });
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  // Check password strength on password change
  useEffect(() => {
    const strength = calculatePasswordStrength(formData.password);
    setPasswordStrength(strength);
    // Check if passwords match whenever either password field changes
    setPasswordsMatch(
      formData.confirmPassword === "" ||
        formData.password === formData.confirmPassword
    );
  }, [formData.password, formData.confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setStatus({ type: "error", message: "Passwords do not match" });
      return;
    }

    if (passwordStrength.score < 40) {
      setStatus({
        type: "error",
        message: "Please choose a stronger password",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${MAIN_URL}/api/v1/himraahi/reset-password`,
        {
          token: token,
          newPassword: formData.password,
        }
      );

      setStatus({ type: "success", message: "Password reset successful!" });
      setTimeout(() => navigate("/login"), 2000);
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          "Invalid or expired reset link. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  return (
    <div className="auth-container">
      <img className="wave" src={WaveImg} alt="background wave" />
      <div className="container">
        <div className="img">
          <img src={Bg} alt="background decoration" />
        </div>
        <div className="login-content">
          <form onSubmit={handleSubmit} className="auth-form">
            <img src={Avtar} alt="avatar" className="auth-avatar" />
            <h2 className="title">Set New Password</h2>

            {status.message && (
              <Box mb={2}>
                <Alert
                  severity={status.type}
                  icon={
                    status.type === "success" ? (
                      <CheckCircleIcon />
                    ) : (
                      <ErrorIcon />
                    )
                  }
                >
                  {status.message}
                </Alert>
              </Box>
            )}
            <PasswordBus
              strength={passwordStrength.score}
              isVisible={showPassword.password}
            />
            <div className="input-div pass">
              <div className="i">
                <LockIcon className="input-icon" />
              </div>

              <div className="div password-input-container">
                <input
                  placeholder="New Password"
                  type={showPassword.password ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="input"
                  disabled={isLoading}
                  required
                  minLength={8}
                />
                <IconButton
                  onClick={() => togglePasswordVisibility("password")}
                  className="visibility-toggle"
                  size="small"
                >
                  {showPassword.password ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </IconButton>
              </div>
            </div>

            {/* Password Strength Indicator */}
            {formData.password && (
              <Box sx={{ width: "100%", mt: 1, mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="caption">Password Strength:</Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: passwordStrength.color, fontWeight: "bold" }}
                  >
                    {passwordStrength.strength.toUpperCase()}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength.score}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "rgba(0,0,0,0.1)",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: passwordStrength.color,
                    },
                  }}
                />
                <List dense sx={{ mt: 1 }}>
                  {Object.entries(passwordStrength.checks).map(
                    ([key, value]) => (
                      <ListItem key={key} sx={{ p: 0 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          {value ? (
                            <CheckIcon
                              sx={{ color: "#4caf50", fontSize: 16 }}
                            />
                          ) : (
                            <CloseIcon
                              sx={{ color: "#f44336", fontSize: 16 }}
                            />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="caption">
                              {key === "length"
                                ? "At least 8 characters"
                                : key === "lowercase"
                                ? "Contains lowercase letter"
                                : key === "uppercase"
                                ? "Contains uppercase letter"
                                : key === "numbers"
                                ? "Contains number"
                                : "Contains special character"}
                            </Typography>
                          }
                        />
                      </ListItem>
                    )
                  )}
                </List>
              </Box>
            )}

            <div className="input-div pass">
              <div className="i">
                <LockIcon className="input-icon" />
              </div>
              <div className="div password-input-container">
                <input
                  placeholder="Confirm New Password"
                  type={showPassword.confirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      confirmPassword: e.target.value,
                    })
                  }
                  className={`input ${!passwordsMatch ? "error" : ""}`}
                  disabled={isLoading}
                  required
                  minLength={8}
                />
                <IconButton
                  onClick={() => togglePasswordVisibility("confirmPassword")}
                  className="visibility-toggle"
                  size="small"
                >
                  {showPassword.confirmPassword ? (
                    <VisibilityOffIcon />
                  ) : (
                    <VisibilityIcon />
                  )}
                </IconButton>
              </div>
            </div>

            {!passwordsMatch && formData.confirmPassword && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 1 }}
              >
                Passwords do not match
              </Typography>
            )}

            <button
              type="submit"
              className={`btn ${isLoading ? "loading" : ""}`}
              disabled={
                isLoading || !passwordsMatch || passwordStrength.score < 40
              }
            >
              {isLoading ? "Resetting Password..." : "Reset Password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
